import { defineStore } from 'pinia';
import api from '@/api/api';
import type { ZRating } from '@schemas/rating.schemas';


export const useRatingStore = defineStore('rating', {
    state: () => ({
        rating: null as ZRating | null,
        ratings: [] as ZRating[],
        myRatingsReceived: undefined as ZRating[] | undefined,
        myRatingsGiven: undefined as ZRating[] | undefined,
        currentRating: null as ZRating | null
    }),
    actions: {
        clearMyData(): void {
            this.myRatingsReceived = undefined;
            this.myRatingsGiven = undefined;
        },
        async fetchRating(ratingId: string): Promise<ZRating> {
            this.rating = this.ratings.find(app => app._id === ratingId) ||
                await api.getDocument<ZRating>('ratings', ratingId);
            return this.rating;
        },

        async fetchRatings(): Promise<ZRating[]> {
            this.ratings = await api.getDocuments<ZRating>('ratings');
            return this.ratings;
        },

        /**
         * Fetch ratings received for the current user
         */
        async fetchMyRatingsReceived(): Promise<ZRating[]> {
            if (this.myRatingsReceived === undefined) {
                const result  = await api.getDocuments<ZRating>('ratings/my/receiver');
                this.myRatingsReceived = result || [];
            }
            return this.myRatingsReceived;
        },

        /**
         * Fetch ratings given by the current user
         */
        async fetchMyRatingsAuthor(): Promise<ZRating[]> {
            if (this.myRatingsGiven === undefined) {
                const result  = await api.getDocuments<ZRating>('ratings/my/author');
                this.myRatingsGiven = result || [];
            }
            return this.myRatingsGiven;
        },

        /**
         * Fetch my rating for a specific profile
         * @param profileId
         */
        async fetchMyRatingForProfile(profileId: string): Promise<ZRating> {
            if (this.currentRating && this.currentRating.userId === profileId) {
                return this.currentRating;
            }
            this.currentRating = await api.getDocument<ZRating>('ratings/my/author/profile', profileId);
            return this.currentRating;
        },
        /**
         * Fetch my rating for a specific event
         * @param eventId
         */
        async fetchMyRatingForEvent(eventId: string): Promise<ZRating> {
            if (this.currentRating && this.currentRating.eventId === eventId) {
                return this.currentRating;
            }
            this.currentRating = await api.getDocument<ZRating>('ratings/my/author/event', eventId);
            return this.currentRating;
        },

        /**
         * Fetch ratings for a specific event
         * @param eventId
         */
        async fetchRatingsForEvent(eventId: string): Promise<ZRating[]> {
            this.ratings = await api.getDocuments<ZRating>('ratings/event/' + eventId);
            return this.ratings;
        },
        /**
         * Fetch latest ratings for a specific user
         * @param userId
         */
        async fetchRatingsForProfile(profileId: string): Promise<ZRating[]> {
            this.ratings = await api.getDocuments<ZRating>('ratings/profile/' + profileId);
            return this.ratings;
        },
        /**
         * Add a new rating
         * There is no update only post.
         * @param rating
         */
        async addRating(rating: Partial<ZRating>): Promise<ZRating> {
            const newRating = await api.postDocument<ZRating>('ratings', rating);
            this.ratings.push(newRating);
            return newRating;
        },

        /**
         * Remove a rating
         * @param ratingId
         */
        async removeRating(ratingId: string): Promise<void> {
            await api.deleteDocument<ZRating>('ratings', ratingId);
            this.ratings = this.ratings.filter(rating => rating._id !== ratingId);
        }
    }
});
